import { useState, useMemo } from 'react';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Pencil, Trash, QrCode, Printer, UserPlus } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useDataCache } from '@/contexts/DataCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NIVELES = {
  precio_1: 'Precio 1',
  precio_2: 'Precio 2',
  precio_3: 'Precio 3',
  precio_4: 'Precio 4',
  precio_5: 'Precio 5',
  mayoreo: 'Mayoreo',
  medio_mayoreo: 'Medio Mayoreo',
};

export const Clients = () => {
  const { clients, refreshClients } = useDataCache();
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [qrModal, setQrModal] = useState(null); // {id, name, qr_code, precio_nivel, parent_client_id}
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    precio_nivel: 'precio_1',
    nip: '',
    parent_client_id: null,
  });

  // Agrupar padre + hijos para render jerárquico
  const grouped = useMemo(() => {
    const byParent = new Map(); // parent_id -> [children]
    const roots = [];
    for (const c of clients) {
      if (c.parent_client_id) {
        if (!byParent.has(c.parent_client_id)) byParent.set(c.parent_client_id, []);
        byParent.get(c.parent_client_id).push(c);
      } else {
        roots.push(c);
      }
    }
    // Aplanar: padre seguido de sus hijos
    const flat = [];
    for (const r of roots) {
      flat.push({ ...r, _depth: 0 });
      for (const child of (byParent.get(r.id) || [])) {
        flat.push({ ...child, _depth: 1, _parent_name: r.name });
      }
    }
    // Hijos huérfanos (padre borrado): al final como root
    for (const c of clients) {
      if (c.parent_client_id && !clients.find(cc => cc.id === c.parent_client_id)) {
        flat.push({ ...c, _depth: 0 });
      }
    }
    return flat;
  }, [clients]);

  const openDialog = (c = null, parentClient = null) => {
    if (c) {
      setEditing(c);
      setFormData({
        name: c.name || '',
        email: c.email || '',
        phone: c.phone || '',
        address: c.address || '',
        precio_nivel: c.precio_nivel || 'precio_1',
        nip: c.nip || '',
        parent_client_id: c.parent_client_id || null,
      });
    } else if (parentClient) {
      // Crear hijo del mayorista padre
      setEditing(null);
      setFormData({
        name: '', email: '', phone: '', address: '',
        precio_nivel: parentClient.precio_nivel || 'mayoreo',
        nip: '',
        parent_client_id: parentClient.id,
        _parent_name: parentClient.name,
      });
    } else {
      setEditing(null);
      setFormData({ name: '', email: '', phone: '', address: '', precio_nivel: 'precio_1', nip: '', parent_client_id: null });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nipTrim = (formData.nip || '').trim();
    const isMayor = ['mayoreo', 'medio_mayoreo'].includes(formData.precio_nivel);
    if (isMayor && nipTrim && !/^\d{4}$/.test(nipTrim)) {
      toast.error('El NIP debe ser 4 dígitos');
      return;
    }
    const payload = { ...formData };
    delete payload._parent_name;
    if (!payload.parent_client_id) delete payload.parent_client_id;
    if (!isMayor || !nipTrim) delete payload.nip;
    else payload.nip = nipTrim;
    try {
      if (editing) {
        await axios.put(`${API}/clients/${editing.id}`, payload, { withCredentials: true });
        toast.success('Cliente actualizado');
      } else {
        const { data } = await axios.post(`${API}/clients`, payload, { withCredentials: true });
        toast.success(`Cliente creado · QR ${data.qr_code}`);
      }
      setShowDialog(false);
      refreshClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar cliente');
    }
  };

  const regenerateNip = async (clientId) => {
    if (!window.confirm('¿Regenerar el NIP? El anterior dejará de funcionar.')) return;
    try {
      const { data } = await axios.post(`${API}/clients/${clientId}/regenerate-nip`, {}, { withCredentials: true });
      toast.success(`Nuevo NIP: ${data.nip}`);
      try { await navigator.clipboard.writeText(data.nip); } catch { /* noop */ }
      refreshClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo regenerar');
    }
  };

  const regenerateQr = async (clientId) => {
    if (!window.confirm('¿Regenerar el QR? El anterior dejará de funcionar.')) return;
    try {
      const { data } = await axios.post(`${API}/clients/${clientId}/regenerate-qr`, {}, { withCredentials: true });
      toast.success(`Nuevo QR: ${data.qr_code}`);
      refreshClients();
      if (qrModal && qrModal.id === clientId) {
        setQrModal({ ...qrModal, qr_code: data.qr_code });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo regenerar QR');
    }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado`);
    } catch { toast.error('No se pudo copiar'); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`¿Eliminar al cliente "${c.name}"?`)) return;
    try {
      await axios.delete(`${API}/clients/${c.id}`, { withCredentials: true });
      toast.success('Cliente eliminado');
      refreshClients();
    } catch { toast.error('Error al eliminar cliente'); }
  };

  const printQR = () => {
    const el = document.getElementById('qr-print-area');
    if (!el) return;
    const w = window.open('', '_blank', 'width=500,height=600');
    if (!w) { toast.error('Permite ventanas emergentes'); return; }
    w.document.write(`
      <html><head><title>QR Cliente</title>
      <style>
        body { font-family: system-ui, sans-serif; text-align: center; padding: 24px; }
        h2 { margin: 0 0 4px; font-size: 20px; }
        h3 { margin: 0 0 20px; color: #666; font-weight: normal; font-size: 14px; }
        .qr { display: inline-block; padding: 16px; border: 2px solid #000; border-radius: 8px; }
        .code { margin-top: 12px; font-family: monospace; font-size: 14px; letter-spacing: 2px; font-weight: bold; }
      </style>
      </head><body>${el.innerHTML}</body></html>
    `);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 300);
  };

  return (
    <MainLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Clientes</h1>
            <p className="text-muted-foreground mt-1">Gestiona tu base de clientes · QR para escanear en POS · Sub-cuentas para mayoristas</p>
          </div>
          <Button onClick={() => openDialog()} data-testid="add-client-button">
            <Plus size={20} weight="bold" className="mr-2" />
            Nuevo Cliente
          </Button>
        </div>

        <div className="bg-white border border-border rounded-sm">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="clients-table">
              <thead className="bg-slate-50 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Nombre</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Teléfono</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Nivel</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">NIP</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">QR</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {grouped.map((client) => {
                  const isChild = !!client.parent_client_id;
                  const isMayor = ['mayoreo', 'medio_mayoreo'].includes(client.precio_nivel);
                  return (
                    <tr key={client.id} className="hover:bg-slate-50 btn-transition" data-testid={`client-row-${client.id}`}>
                      <td className="px-4 py-3 text-sm font-medium">
                        {isChild && <span className="text-amber-500 mr-1">↳</span>}
                        {isChild && client._parent_name && (
                          <span className="text-xs text-slate-400 mr-1">{client._parent_name} —</span>
                        )}
                        {client.name}
                        {isChild && <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5 font-bold">HIJO</span>}
                      </td>
                      <td className="px-4 py-3 text-sm">{client.phone || '-'}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${isMayor ? 'bg-purple-100 text-purple-700' : 'bg-primary/10 text-primary'}`}>
                          {NIVELES[client.precio_nivel] || 'Precio 1'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {isMayor ? (
                          client.nip ? (
                            <div className="flex items-center gap-1.5">
                              <code
                                className="text-xs font-mono font-bold bg-purple-50 text-purple-700 border border-purple-200 px-2 py-1 rounded tracking-widest cursor-pointer"
                                onClick={() => copyText(client.nip, 'NIP')}
                                title="Click para copiar"
                              >{client.nip}</code>
                              <button
                                type="button"
                                onClick={() => regenerateNip(client.id)}
                                title="Regenerar NIP"
                                className="p-1 hover:bg-amber-100 rounded text-amber-600"
                              >🔄</button>
                            </div>
                          ) : <span className="text-xs text-muted-foreground">Sin NIP</span>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {client.qr_code ? (
                          <button
                            type="button"
                            onClick={() => setQrModal(client)}
                            className="inline-flex items-center gap-1.5 text-xs font-mono font-bold bg-sky-50 text-sky-700 border border-sky-200 px-2 py-1 rounded cursor-pointer hover:bg-sky-100"
                            data-testid={`view-qr-${client.id}`}
                            title="Ver / imprimir QR"
                          >
                            <QrCode size={14} weight="bold" />
                            {client.qr_code}
                          </button>
                        ) : (
                          <span className="text-xs text-muted-foreground">Sin QR</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <div className="flex gap-1 justify-end flex-wrap">
                          {isMayor && !isChild && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openDialog(null, client)}
                              data-testid={`add-child-${client.id}`}
                              title="Agregar sub-cuenta (hijo)"
                              className="text-amber-700 border-amber-300 hover:bg-amber-50"
                            >
                              <UserPlus size={14} weight="bold" className="mr-1" /> Hijo
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => openDialog(client)} data-testid={`edit-client-${client.id}`}>
                            <Pencil size={14} />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDelete(client)} data-testid={`delete-client-${client.id}`}>
                            <Trash size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {grouped.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-muted-foreground">
                      No hay clientes registrados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Dialog crear/editar */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? 'Editar Cliente'
                : formData.parent_client_id
                  ? `Nueva Sub-cuenta (Hijo de ${formData._parent_name || '?'})`
                  : 'Nuevo Cliente'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="client-form">
            <div>
              <Label htmlFor="name">Nombre *</Label>
              <Input id="name" value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="client-name-input" required />
              {formData.parent_client_id && (
                <p className="text-[11px] text-amber-700 mt-1">
                  En reportes y POS se mostrará como <b>&quot;{formData._parent_name} - {formData.name || '…'}&quot;</b>
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="client-email-input" />
              </div>
              <div>
                <Label htmlFor="phone">Teléfono</Label>
                <Input id="phone" value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="client-phone-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="address">Dirección</Label>
              <Input id="address" value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                data-testid="client-address-input" />
            </div>
            {!formData.parent_client_id && (
              <div>
                <Label htmlFor="precio_nivel">Nivel de Precio *</Label>
                <Select value={formData.precio_nivel} onValueChange={(value) => setFormData({ ...formData, precio_nivel: value })}>
                  <SelectTrigger data-testid="client-precio-nivel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(NIVELES).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {formData.parent_client_id && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
                🔗 El nivel de precio se hereda del padre (<b>{NIVELES[formData.precio_nivel]}</b>). No editable aquí.
              </div>
            )}
            {['mayoreo', 'medio_mayoreo'].includes(formData.precio_nivel) && (
              <div>
                <Label htmlFor="client_nip" className="flex items-center gap-2">
                  NIP (4 dígitos)
                  <span className="text-[10px] text-slate-500 font-normal">— para portal <code>/mayorista</code></span>
                </Label>
                <Input id="client_nip" type="text" inputMode="numeric" maxLength={4}
                  placeholder={editing?.nip || 'Se autogenera al guardar si lo dejas vacío'}
                  value={formData.nip}
                  onChange={(e) => setFormData({ ...formData, nip: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                  data-testid="client-nip-input"
                  className="font-mono tracking-widest" />
                <p className="text-xs text-slate-500 mt-1">Debe ser único entre clientes mayoristas.</p>
              </div>
            )}
            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} data-testid="cancel-client-button">Cancelar</Button>
              <Button type="submit" data-testid="save-client-button">{editing ? 'Actualizar' : 'Crear'} Cliente</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog QR */}
      <Dialog open={!!qrModal} onOpenChange={(o) => !o && setQrModal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>QR Cliente</DialogTitle>
          </DialogHeader>
          {qrModal && (
            <div className="space-y-3">
              <div id="qr-print-area" className="text-center">
                <h2>{qrModal.parent_client_id && qrModal._parent_name ? `${qrModal._parent_name} - ` : ''}{qrModal.name}</h2>
                <h3>{NIVELES[qrModal.precio_nivel] || qrModal.precio_nivel}</h3>
                <div className="qr inline-flex items-center justify-center bg-white">
                  <QRCodeCanvas value={qrModal.qr_code} size={220} includeMargin={true} />
                </div>
                <div className="code">{qrModal.qr_code}</div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => copyText(qrModal.qr_code, 'QR')} data-testid="copy-qr-button">Copiar código</Button>
                <Button variant="outline" onClick={() => regenerateQr(qrModal.id)} data-testid="regenerate-qr-button" className="text-amber-700 border-amber-300 hover:bg-amber-50">🔄 Regenerar</Button>
                <Button onClick={printQR} data-testid="print-qr-button"><Printer size={16} className="mr-1" /> Imprimir</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};
